import React, { useRef, useState, useEffect } from "react";

import { animated, useSpring } from "@react-spring/web";
import { isEmpty } from "lodash/fp";

import Box from "@mui/material/Box";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Typography, { TypographyProps } from "@mui/material/Typography";

import useMeasure from "@app/common/hooks/useMeasure";

import { EditButton, CloseButton, Wrapper } from "./styles";

export interface EditTypographyProps extends Omit<TypographyProps, "children"> {
  value: string;
  editOn?: "click" | "focus" | "mount";
  onEditChange: (text: string) => void;
  /** whether the edit/save buttons float (absolute positioned) or are styled inline */
  floatingButtons?: boolean;
  maxWidth?: number;
  minWidth?: number;
  onToggle?: (isEditing: boolean) => void;
}

const AnimatedTypography = animated((props) => (
  <Typography component="span" {...props} />
));
const AnimatedOutlinedInput = animated(OutlinedInput);
const AnimatedBox = animated(Box);
const AnimatedStack = animated(Stack);

// This component uses a few hardcoded values to determine how much room
// there is to display and edit the text.
// 32 - Is the width of the edit button width padding
// 64 - Is the width of the save and close buttons with padding
// 72 - Is the max width of the button cluster plus 8 pixels of padding
// 74 - Is the max width of the button cluster plus 8 pixels of padding and the input border

const DEFAULT_MIN_WIDTH = 200;

const EditTypography = ({
  editOn = "click",
  value,
  onEditChange,
  onToggle,
  floatingButtons = false,
  maxWidth,
  minWidth = DEFAULT_MIN_WIDTH,
  ...typographyProps
}: EditTypographyProps) => {
  const [isEditing, setIsEditingState] = useState(false);
  const setIsEditing = (isEditing: boolean) => {
    onToggle?.(isEditing);
    setIsEditingState(isEditing);
  };

  const [inputValue, setInputValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const heightRef = useRef<number>(0);

  const [measureRef, { width: elWidth, height: elHeight }] =
    useMeasure<HTMLElement>();

  // Prevents setting the text height to 0 when there is
  // no text in the input
  heightRef.current = elHeight > 0 ? elHeight : heightRef.current;
  const height = heightRef.current;

  // width of the outer content adds padding around reference input
  const elWidthWithPadding = elWidth + (isEditing ? 114 : 80);
  const width = maxWidth
    ? Math.min(
        Math.max(elWidthWithPadding, minWidth),
        // increase the max width if the buttons are floating and we are not in
        // edit mode. This will allow the text to be longer before it is truncated
        // since the edit icon floats
        maxWidth + (floatingButtons ? (isEditing ? 0 : 40) : 0)
      )
    : elWidthWithPadding;

  useEffect(() => {
    if (editOn === "mount") {
      setIsAnimating(true);
      setIsEditing(true);
    }
  }, []);

  useEffect(() => {
    if (!isEditing) {
      setInputValue(value);
    }
  }, [value]);

  const handleSave = () => {
    if (isEmpty(inputValue)) {
      return;
    }
    setIsAnimating(true);
    setIsEditing(!isEditing);
    if (inputValue !== value) {
      onEditChange(inputValue);
    }
  };

  const handleToggle = () => {
    setIsAnimating(true);
    setIsEditing(!isEditing);
    setInputValue(value);
  };

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    return isEditing ? handleSave() : handleToggle();
  };

  const handleWrapperClick = (event: React.MouseEvent) => {
    if (isEditing || editOn === "click") {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!isEditing && editOn === "click") {
      setIsAnimating(true);
      setIsEditing(true);
    }
  };

  const handleInputFocus = (event: React.FocusEvent) => {
    if (editOn !== "focus") return;

    event.preventDefault();
    event.stopPropagation();
    if (!isEditing) {
      setIsAnimating(true);
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    if (isAnimating || !isEditing || inputValue !== value) return;
    // if there was no change to the value, simply exit the editing state
    setIsAnimating(true);
    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSave();
    } else if (event.key === "Escape") {
      handleToggle();
    }
  };

  const typographyStyle = useSpring({
    to: {
      width: width === 0 ? "auto" : width - 72,
    },
    immediate: !isInitialized,
  });

  const labelStyle = useSpring({
    to: { opacity: isEditing ? 0 : 1, marginLeft: isEditing ? 8 : 0 },
    immediate: !isInitialized,
  });

  const buttonsStyle = useSpring({
    to: { right: isEditing ? -64 : -32, width: isEditing ? 64 : 32 },
    immediate: !isInitialized,
  });

  const inputStyle = useSpring({
    to: {
      left: isEditing ? 0 : -7,
      opacity: isEditing ? 1 : 0,
      width: width === 0 ? "auto" : width - 72,
    },
    immediate: !isInitialized,
    onRest: () => {
      setIsAnimating(false);
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
      }
    },
  });

  // Prevent an awkward animation when
  // refs are being set up. Then allow
  // animations
  useEffect(() => {
    if (!isInitialized && elWidth > 0) {
      setIsInitialized(true);
    }
  }, [Boolean(elWidth)]);

  return (
    <Wrapper
      editing={isEditing || isAnimating}
      onClick={handleWrapperClick}
      className={`editable-typography${
        editOn === "click" ? " editable-typography--clickable" : ""
      }`}
    >
      <AnimatedTypography
        {...typographyProps}
        style={typographyStyle}
        sx={{
          position: "relative",
          cursor: "pointer",
          height,
        }}
      >
        <AnimatedOutlinedInput
          className="editable-typography__input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={editOn === "focus" ? handleInputFocus : undefined}
          inputRef={inputRef}
          sx={{
            pointerEvents: isEditing ? undefined : "none",
            position: "absolute",
            top: -2,
            fontSize: "inherit",
            lineHeight: "inherit",
            fontWeight: "inherit",
            "& .MuiInputBase-input": {
              height,
              lineHeight: "inherit",
              fontSize: "inherit",
              fontWeight: "inherit",
              padding: "2px 8px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: (theme) =>
                `1px solid ${theme.palette.shades.neutral[100]}`,
            },
          }}
          style={inputStyle}
        />
        <AnimatedBox
          style={labelStyle}
          component="span"
          className="editable-typography__value"
          sx={{
            display: "inline-block",
            pointerEvents: isEditing ? "none" : undefined,
            maxWidth: width - 72 - (floatingButtons && !isEditing ? 8 : 0),
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {inputValue}
        </AnimatedBox>
      </AnimatedTypography>

      <Typography
        {...typographyProps}
        component="span"
        sx={{
          opacity: 0,
          position: "absolute",
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
        ref={measureRef}
      >
        {inputValue}
      </Typography>
      <AnimatedStack
        direction="row"
        alignItems="center"
        spacing={1}
        style={buttonsStyle}
        sx={
          floatingButtons
            ? { position: "absolute", overflow: "hidden", paddingLeft: "6px" }
            : { overflow: "hidden" }
        }
      >
        <EditButton
          className={`editable-typography__edit-button${
            isEditing ? " editing" : ""
          }${editOn !== "click" && !isEditing ? " hidden" : ""}`}
          onClick={handleClick}
          editing={isEditing}
          disabled={isEditing && isEmpty(inputValue)}
        />
        {isEditing && <CloseButton onClick={handleToggle} />}
      </AnimatedStack>
    </Wrapper>
  );
};

export default EditTypography;
